import { useState, useCallback, useContext } from "react";
import { Dropdown } from "~/components/UI";
import WalletsCredentialsContext from "./walletsCredentialsContext";
import { useTranslation } from "react-i18next";
import { SOURCE_TYPE } from "~/api/common";
import useCreateIncidentDialog from "~/components/Incidents/CreateIncident/useCreateIncidentDialog";
import { PERMISSION } from "~/api/permissions/enums";
import usePermissions from "~/hook/usePermissions";

interface WalletOptionsProps {
  wallet: WalletsApi.WalletDto;
}

/**
 * @memberof WalletsCredentialsPage
 * @component
 * @desc Wallet options. Using a walletsCredentialsContext.
 * @property {WalletsApi.WalletDto} wallet - Wallet data;
 * @requires WalletsContext
 */

const WalletOptions = ({ wallet }: WalletOptionsProps) => {
  const [loading, setLoading] = useState(false);
  const { onEdit, onUpdate, onUpdateItem, onDelete } = useContext(
    WalletsCredentialsContext
  );
  const [createIncident] = useCreateIncidentDialog(SOURCE_TYPE.WALLETS);
  const { enabled } = wallet;
  const { hasAll, hasAny } = usePermissions();
  const { t } = useTranslation();

  const onUpdateHandler = useCallback((res: Promise<unknown> | void) => {
    if (res) {
      setLoading(true);
      res.finally(() => {
        setLoading(false);
      });
    }
  }, []);

  const onCreatedIncident = useCallback(
    (incident: IncidentsApi.IncidentDto) => {
      if (wallet.incidents) {
        wallet.incidents.count++;
        wallet.incidents.incidentIds.push(incident.incidentId);
      } else {
        wallet.incidents = {
          count: 1,
          incidentIds: [incident.incidentId],
        };
      }
      onUpdateItem && onUpdateItem(wallet);
    },
    [wallet, onUpdateItem]
  );

  if (
    !hasAny([
      PERMISSION.EDIT_WALLET_CREDENTIALS,
      PERMISSION.DELETE_WALLET_CREDENTIALS,
      PERMISSION.CREATE_JOURNAL_INCIDENTS,
    ])
  ) {
    return null;
  }

  const options = [
    {
      label: enabled ? t("option.disable") : t("option.enable"),
      hidden: !hasAll(PERMISSION.EDIT_WALLET_CREDENTIALS),
      confirmation: {
        cancelText: t("button.no"),
        okText: t("button.yes"),
        title: t("text.confirmationRequest"),
        onConfirm: () =>
          onUpdate &&
          onUpdateHandler(
            onUpdate(wallet.walletId, {
              enabled: !enabled,
            } as WalletsApi.UpdateWallet)
          ),
      },
    },
    {
      label: t("option.edit"),
      hidden: !hasAll(PERMISSION.EDIT_WALLET_CREDENTIALS),
      onClick: () => onEdit && onEdit(wallet),
    },
    {
      label: t("option.delete"),
      hidden: !hasAll(PERMISSION.DELETE_WALLET_CREDENTIALS),
      onClick: () => onDelete && onUpdateHandler(onDelete(wallet)),
    },
    {
      label: t("option.createIncident"),
      hidden: !hasAll(PERMISSION.CREATE_JOURNAL_INCIDENTS),
      onClick: () => createIncident(wallet.walletId, onCreatedIncident),
    },
  ];

  return <Dropdown loading={loading} options={options} />;
};

export default WalletOptions;
